import { useState } from "react";
import ExcelJS from "exceljs";
import {
  RFID_EXPORT_COLUMNS,
  RFID_HISTORY_COLUMNS,
  getExportDateTime,
  getHistoryForRow,
  loadAllRfidHistory,
  mapHistoryRowsForExport,
  mapRfidRowsForExport,
} from "./rfidExportHelpers";
import { useRfidStore } from "./store/useRfidStore";

const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "003366" },
};

const HISTORY_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "DBEAFE" },
};

const centerCell = (cell) => {
  cell.alignment = { horizontal: "center" };
};

const styleHeaderRow = (row, fill, columnCount) => {
  const boldFont = { bold: true, color: { argb: "FFFFFFFF" } };
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    cell.font = boldFont;
    centerCell(cell);
    if (colNumber <= columnCount) {
      cell.fill = fill;
    }
  });
};

export const useRfidExcelExport = (rfidData = []) => {
  const fetchRfidHistory = useRfidStore((state) => state.fetchRfidHistory);
  const [isExporting, setIsExporting] = useState(false);

  const loadAllHistory = () => loadAllRfidHistory(rfidData, fetchRfidHistory);

  const exportToExcel = async () => {
    setIsExporting(true);

    try {
      const historyById = await loadAllHistory();
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("RFID List");
      const boldFont = { bold: true };
      const mainColumnCount = RFID_EXPORT_COLUMNS.length;
      const historyColumnCount = RFID_HISTORY_COLUMNS.length;

      worksheet.addRow(["", "", getExportDateTime(), ""]);
      worksheet.mergeCells(1, 2, 1, mainColumnCount);
      worksheet.getCell(1, 2).font = boldFont;
      worksheet.getCell(1, 2).alignment = { horizontal: "center" };

      worksheet.addRow(["", "", "RFID List", ""]);
      worksheet.mergeCells(2, 2, 2, mainColumnCount);
      worksheet.getCell(2, 2).font = boldFont;
      worksheet.getCell(2, 2).alignment = { horizontal: "center" };

      const mainHeaderRow = worksheet.addRow(RFID_EXPORT_COLUMNS);
      styleHeaderRow(mainHeaderRow, HEADER_FILL, mainColumnCount);

      const mainRows = mapRfidRowsForExport(rfidData);
      mainRows.forEach((mainRow, index) => {
        const device = rfidData[index];
        const history = getHistoryForRow(device, historyById);

        worksheet.addRow(mainRow).eachCell({ includeEmpty: true }, centerCell);

        if (!history.length) return;

        const historyTitleRow = worksheet.addRow([
          "",
          `History - ${device.rfidCode || "N/A"}`,
        ]);
        worksheet.mergeCells(
          historyTitleRow.number,
          2,
          historyTitleRow.number,
          1 + historyColumnCount
        );
        const titleCell = historyTitleRow.getCell(2);
        titleCell.font = boldFont;
        titleCell.fill = HISTORY_HEADER_FILL;

        const historyHeaderRow = worksheet.addRow([
          "",
          ...RFID_HISTORY_COLUMNS,
        ]);
        styleHeaderRow(historyHeaderRow, HISTORY_HEADER_FILL, historyColumnCount + 1);

        mapHistoryRowsForExport(history).forEach((historyRow) => {
          worksheet
            .addRow(["", ...historyRow])
            .eachCell({ includeEmpty: true }, centerCell);
        });

        worksheet.addRow([]);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "RfidList.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating Excel file:", error);
    } finally {
      setIsExporting(false);
    }
  };

  return { exportToExcel, isExporting };
};

export const ExportRfidExcel = ({ rfidData = [] }) => {
  const { exportToExcel, isExporting } = useRfidExcelExport(rfidData);

  return (
    <button
      type="button"
      onClick={exportToExcel}
      className="button-container ms-0"
      disabled={isExporting}
    >
      <i className="fa-solid fa-file-excel me-1 icon-container"></i>
      Excel
    </button>
  );
};
